import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const MobileBottomNav: React.FC = () => {
    const { user } = useAuth();
    const location = useLocation();
    const currentPath = location.pathname;

    // Hide on project details pages
    if (currentPath.includes('/project-details/')) {
        return null;
    }

    const getAccountLink = () => {
        if (!user) return "/login";
        switch (user.role) {
            case 'admin': return "/admin-dashboard";
            case 'developer': return "/developer-dashboard";
            case 'broker': return "/broker-dashboard";
            case 'owner': return "/owner-dashboard";
            case 'agent': return "/agent-dashboard";
            case 'buyer': return "/buyer-dashboard";
            case 'tenant': return "/tenant-dashboard";
            default: return "/dashboard";
        }
    };

    const navItems = [
        { 
            label: 'Home', 
            icon: 'fa-solid fa-house', 
            path: '/',
            isActive: currentPath === '/'
        },
        { 
            label: 'Property', 
            icon: 'fa-solid fa-building-user', 
            path: '/project-list',
            isActive: currentPath === '/project-list'
        },
        { 
            label: 'Whatsapp', 
            icon: 'fa-brands fa-whatsapp', 
            path: 'https://wa.me/919537002666',
            isExternal: true,
            isActive: false
        },
        { 
            label: 'Account', 
            icon: 'fa-solid fa-user-circle', 
            path: getAccountLink(),
            isActive: currentPath.includes('dashboard') || currentPath === '/login'
        }
    ];

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[1000] lg:hidden pb-safe pointer-events-none">
            <div className="w-full h-20 border-t border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.3)] flex items-center justify-around px-2 pointer-events-auto relative overflow-hidden" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>
                
                {/* Decorative Background Glows */}
                <div className="absolute top-0 left-1/4 w-24 h-24 bg-[#f1ae24]/10 rounded-full blur-[40px] pointer-events-none"></div>
                <div className="absolute bottom-0 right-1/4 w-24 h-24 bg-white/5 rounded-full blur-[30px] pointer-events-none"></div>

                {navItems.map((item, idx) => {
                    const Content = (
                        <div className="flex flex-col items-center justify-center gap-1 relative py-2">
                            {item.isActive && (
                                <motion.div 
                                    layoutId="activeTabGlow"
                                    className="absolute inset-0 bg-[#f1ae24]/20 rounded-2xl blur-xl"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            
                            <div className={`text-2xl transition-all duration-500 relative z-10 ${item.isActive ? 'text-[#f1ae24] scale-110' : 'text-slate-400 group-active:scale-90'}`}>
                                <i className={item.icon}></i>
                            </div>
                            
                            <span className={`text-[10px] font-black leading-none relative z-10 transition-colors duration-500 ${item.isActive ? 'text-white' : 'text-slate-500'}`}>
                                {item.label}
                            </span>

                            {item.isActive && (
                                <motion.div 
                                    layoutId="activeTabIndicator"
                                    className="absolute -top-1 w-1 h-1 bg-[#f1ae24] rounded-full shadow-[0_0_10px_#f1ae24]"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                        </div>
                    );

                    if (item.isExternal) {
                        return (
                            <a 
                                key={idx} 
                                href={item.path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 group"
                            >
                                {Content}
                            </a>
                        );
                    }

                    return (
                        <Link 
                            key={idx} 
                            to={item.path} 
                            className="flex-1 group"
                        >
                            {Content}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default MobileBottomNav;
